import { useTrackingContext } from '@liander/context';
import React from 'react';
import { WizardSimpleOptions } from 'src/context/Tracking/Wizard';

type BaseFormOptions = { formName: string; siteSection: string; stepIndex?: number };
export type SharedBaseFormOptions = Pick<BaseFormOptions, 'formName' | 'stepIndex'>;

const getBaseFormTrackingData = ({ formName, siteSection }: BaseFormOptions) => ({
  actionType: 'form',
  pageCategory: 'not available',
  siteSection,
  formName: formName ?? 'not available',
  pagePath: window.location.pathname,
  pageName: window.document.title ?? 'not available',
  applicationName: 'liander.nl',
});

type FormStartOptions = SharedBaseFormOptions & { actionDetail: string };
export const trackFormStart =
  (siteSection: string) =>
  ({ formName, actionDetail, stepIndex }: FormStartOptions) => {
    window._satellite.track(`custom_track_form`, {
      ...getBaseFormTrackingData({ formName, siteSection }),
      action: 'start',
      actionDetail,
      formStep: stepIndex ? stepIndex : `not available`,
    });
  };

type FormErrorsOptions = SharedBaseFormOptions & { actionDetail: string };

export const trackFormErrors =
  (siteSection: string) =>
  ({ formName, stepIndex, actionDetail }: FormErrorsOptions) => {
    window._satellite.track(`custom_track_form`, {
      ...getBaseFormTrackingData({ formName, siteSection }),
      action: 'field validation error',
      actionDetail: `form '${formName}': validation error: ${actionDetail}`,
      formStep: stepIndex ? stepIndex : `not available`,
    });
  };

export const trackSubmitSuccess =
  (siteSection: string) =>
  ({ formName, stepIndex }: SharedBaseFormOptions) => {
    window._satellite.track(`custom_track_form`, {
      ...getBaseFormTrackingData({ formName, siteSection }),
      action: `submit`,
      actionDetail: `form '${formName}': submit`,
      formStep: stepIndex ? stepIndex : `not available`,
    });
  };

type FormErrorOptions = SharedBaseFormOptions & { errorString: string };
export const trackSubmitErrors =
  (siteSection: string) =>
  ({ formName, stepIndex, errorString }: FormErrorOptions) => {
    window._satellite.track('custom_track_form', {
      ...getBaseFormTrackingData({ formName, siteSection }),
      action: 'error',
      actionDetail: `form '${formName}' validation error: ${errorString}`,
      formStep: stepIndex ? stepIndex : `not available`,
    });
  };

type FormProceed = SharedBaseFormOptions & { stepName?: string; stepIndex: number };
export const trackSubmitProceed =
  (siteSection: string) =>
  ({ formName, stepIndex, stepName }: FormProceed) => {
    window._satellite.track('custom_track_form', {
      ...getBaseFormTrackingData({ formName, siteSection }),
      action: 'proceed',
      actionDetail: stepName ? `form '${formName}': ${stepName}` : `not available`,
      formStep: stepIndex ? stepIndex : `not available`,
    });
  };

type TrackFormErrors = {
  stepIndex: number;
  fieldsErrors: string[];
  formName: string;
} & Omit<WizardSimpleOptions, 'action' | 'wizardName'>;

/**
 * Hook to track form field validation errors. It monitors field errors and logs them when they change.
 * @param fieldsErrors Array of error messages from form fields
 * @param stepIndex Current step/page number in the form
 * @param formName Name of the form being tracked
 * @param rest Additional wizard options excluding 'action' and 'wizardName'
 *
 * @return void
 */
export const useTrackingFormFieldError = ({
  fieldsErrors,
  stepIndex,
  formName,
  ...rest
}: TrackFormErrors) => {
  const { trackFormErrors } = useTrackingContext();
  const [loggedErrors, setLoggedErrors] = React.useState<string>('');

  React.useEffect(() => {
    const hasErrors = fieldsErrors.some((error) => Boolean(error));
    const newErrors = fieldsErrors.join(', ');

    if (hasErrors && loggedErrors !== newErrors) {
      trackFormErrors({
        ...rest,
        formName: formName,
        actionDetail: `${rest.actionDetail}: ${fieldsErrors.join(', ')}`,
        stepIndex,
      });

      setLoggedErrors(newErrors);
    }
  }, [fieldsErrors, loggedErrors, rest, stepIndex, trackFormErrors, formName]);
};

type TrackTouchedFields = {
  formName: string;
  formStep: number;
  fields: { [key: string]: boolean | boolean[] | undefined };
};

/**
 * Watches the first touched event, the isTouched state is here to prevent multiple triggers on the first touched event.
 * @param wizardName Name of the form/wizard
 * @param wizardStep: Steps of the form/wizard flow mostly '1'
 * @param fields: Fields of the form
 *
 * @return void
 */
export const useTrackingFormTouch = ({ fields, formStep, formName }: TrackTouchedFields): void => {
  const { trackFormStart } = useTrackingContext();
  const [isTouched, setIsTouched] = React.useState<boolean>(false);
  const touchedFields = Object.values(fields);

  React.useEffect(() => {
    if (!isTouched && touchedFields.length === 1) {
      setIsTouched(true);

      trackFormStart({
        actionDetail: `Start ${formName}`,
        formName: formName,
        stepIndex: formStep,
      });
    }
  }, [touchedFields, trackFormStart, isTouched, formName, formStep]);
};

